import { useGetSupervisorPhotoUrl } from '@/common/api/endpoints/oral-exams/oral-exams';
import type { GetSupervisorPhotoUrlParams } from '@/common/api/models/getSupervisorPhotoUrlParams';
import type { OralExamRouteIds } from '@/common/hooks/oralExam/useOralExamRunStatePoll';

/**
 * Resolves a supervisor photo blob path to a short-lived SAS URL via the oral exam API.
 * Returns null when no blob path is provided or when the backend has no URL to serve.
 *
 * `blobPath` is only used to gate the query by default. Pass `forceEnabled: true`
 * (e.g. the lecturer's own presence-setup gate) when the only local signal for
 * "a saved photo might exist" is a client-cached field (`useAuthStore`'s
 * `user.profilePhotoBlobPath`, populated at login and refreshed after capture).
 * Exam avatars are always the lecturer account photo; pass `lecturerId` when
 * resolving a specific supervisor's face (multi-supervisor sittings).
 */
export type SupervisorPhotoResolution = {
  /** Short-lived SAS URL, or null when there is nothing (resolvable) to show. */
  url: string | null;
  /** True while the backend lookup is still in flight (query enabled + loading). */
  isResolving: boolean;
};

/**
 * Richer variant of {@link useSupervisorPhotoUrl} that also reports whether the
 * backend lookup is still in flight. Callers that must decide "reuse saved photo
 * vs. capture a new one" (the lecturer presence-setup gate) need the loading
 * signal so they neither claim a saved photo they cannot render nor prematurely
 * open the camera before the answer is known.
 */
export function useSupervisorPhotoResolution(
  ids: OralExamRouteIds | null,
  blobPath: string | null | undefined,
  options?: { forceEnabled?: boolean; lecturerId?: string | null },
): SupervisorPhotoResolution {
  const enabled = !!ids && (options?.forceEnabled || !!blobPath);
  const params: GetSupervisorPhotoUrlParams | undefined = options?.lecturerId
    ? { lecturer_id: options.lecturerId }
    : undefined;
  const query = useGetSupervisorPhotoUrl(
    ids?.courseId ?? '',
    ids?.moduleId ?? '',
    ids?.taskId ?? '',
    ids?.bookingId ?? '',
    params,
    { query: { enabled } },
  );
  return {
    url: query.data?.data?.data?.url ?? null,
    isResolving: enabled && query.isLoading,
  };
}

export function useSupervisorPhotoUrl(
  ids: OralExamRouteIds | null,
  blobPath: string | null | undefined,
  options?: { forceEnabled?: boolean },
): string | null {
  return useSupervisorPhotoResolution(ids, blobPath, options).url;
}
